import { supabase } from '../lib/supabase';

export const supportTicketService = {
  // Get all support tickets
  async getAllTickets() {
    try {
      const { data, error } = await supabase
        ?.from('support_tickets')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        ticketNumber: row?.ticket_number,
        companyId: row?.company_id,
        issueType: row?.issue_type,
        priority: row?.priority,
        ticketStatus: row?.ticket_status,
        subject: row?.subject,
        description: row?.description,
        clientName: row?.client_name,
        clientEmail: row?.client_email,
        clientPhone: row?.client_phone,
        assignedAgent: row?.assigned_agent,
        assignedTeam: row?.assigned_team,
        slaStatus: row?.sla_status,
        firstResponseTime: row?.first_response_time,
        resolutionTime: row?.resolution_time,
        satisfactionRating: row?.satisfaction_rating,
        satisfactionFeedback: row?.satisfaction_feedback,
        tags: row?.tags,
        createdAt: row?.created_at,
        updatedAt: row?.updated_at,
        resolvedAt: row?.resolved_at,
        closedAt: row?.closed_at
      })) || [];
    } catch (error) {
      console.error('Error fetching support tickets:', error);
      throw error;
    }
  },

  // Get support analytics
  async getSupportAnalytics() {
    try {
      const { data: tickets, error } = await supabase
        ?.from('support_tickets')
        ?.select('*');

      if (error) throw error;

      const totalTickets = tickets?.length || 0;
      const openTickets = tickets?.filter(t => t?.ticket_status === 'open')?.length || 0;
      const inProgressTickets = tickets?.filter(t => t?.ticket_status === 'in_progress')?.length || 0;
      const resolvedTickets = tickets?.filter(t => t?.ticket_status === 'resolved')?.length || 0;
      const closedTickets = tickets?.filter(t => t?.ticket_status === 'closed')?.length || 0;

      // Calculate average resolution time (in hours)
      const resolvedWithTime = tickets?.filter(t => t?.resolution_time) || [];
      const avgResolutionTime = resolvedWithTime?.length > 0
        ? resolvedWithTime?.reduce((sum, t) => sum + (t?.resolution_time || 0), 0) / resolvedWithTime?.length
        : 0;

      // Calculate average satisfaction rating
      const ratedTickets = tickets?.filter(t => t?.satisfaction_rating) || [];
      const avgSatisfaction = ratedTickets?.length > 0
        ? ratedTickets?.reduce((sum, t) => sum + (t?.satisfaction_rating || 0), 0) / ratedTickets?.length
        : 0;

      // First contact resolution rate
      const firstContactResolved = tickets?.filter(t => 
        t?.ticket_status === 'resolved' && t?.first_response_time && t?.resolution_time && 
        t?.first_response_time === t?.resolution_time
      )?.length || 0;
      const firstContactRate = resolvedTickets > 0 ? (firstContactResolved / resolvedTickets) * 100 : 0;

      // SLA compliance
      const withinSLA = tickets?.filter(t => t?.sla_status === 'within_sla')?.length || 0;
      const slaCompliance = totalTickets > 0 ? (withinSLA / totalTickets) * 100 : 0;

      return {
        totalTickets,
        openTickets,
        inProgressTickets,
        resolvedTickets,
        closedTickets,
        avgResolutionTimeHours: Math.round(avgResolutionTime * 10) / 10,
        avgSatisfactionRating: Math.round(avgSatisfaction * 10) / 10,
        firstContactResolutionRate: Math.round(firstContactRate * 10) / 10,
        slaComplianceRate: Math.round(slaCompliance * 10) / 10
      };
    } catch (error) {
      console.error('Error fetching support analytics:', error);
      throw error;
    }
  },

  // Get ticket statistics (alias for getSupportAnalytics)
  async getTicketStatistics() {
    return this.getSupportAnalytics();
  },

  // Get agent performance metrics
  async getAgentPerformance() {
    try {
      const { data: tickets, error } = await supabase
        ?.from('support_tickets')
        ?.select('*');

      if (error) throw error;

      // Group tickets by agent
      const agentMap = {};
      tickets?.forEach(ticket => {
        const agent = ticket?.assigned_agent || 'Unassigned';
        if (!agentMap?.[agent]) {
          agentMap[agent] = {
            agentName: agent,
            totalTickets: 0,
            resolvedTickets: 0,
            avgResolutionTime: 0,
            avgSatisfaction: 0,
            resolutionTimes: [],
            satisfactionRatings: []
          };
        }

        agentMap[agent].totalTickets++;
        if (ticket?.ticket_status === 'resolved' || ticket?.ticket_status === 'closed') {
          agentMap[agent].resolvedTickets++;
        }
        if (ticket?.resolution_time) {
          agentMap?.[agent]?.resolutionTimes?.push(ticket?.resolution_time);
        }
        if (ticket?.satisfaction_rating) {
          agentMap?.[agent]?.satisfactionRatings?.push(ticket?.satisfaction_rating);
        }
      });

      // Calculate averages
      const agentPerformance = Object.values(agentMap)?.map(agent => {
        const avgResTime = agent?.resolutionTimes?.length > 0
          ? agent?.resolutionTimes?.reduce((sum, t) => sum + t, 0) / agent?.resolutionTimes?.length
          : 0;
        const avgSat = agent?.satisfactionRatings?.length > 0
          ? agent?.satisfactionRatings?.reduce((sum, r) => sum + r, 0) / agent?.satisfactionRatings?.length
          : 0;

        return {
          agentName: agent?.agentName,
          totalTickets: agent?.totalTickets,
          resolvedTickets: agent?.resolvedTickets,
          avgResolutionTimeHours: Math.round(avgResTime * 10) / 10,
          avgSatisfactionRating: Math.round(avgSat * 10) / 10,
          resolutionRate: agent?.totalTickets > 0 
            ? Math.round((agent?.resolvedTickets / agent?.totalTickets) * 100 * 10) / 10
            : 0
        };
      });

      return agentPerformance?.sort((a, b) => b?.totalTickets - a?.totalTickets);
    } catch (error) {
      console.error('Error fetching agent performance:', error);
      throw error;
    }
  },

  // Get issue category breakdown
  async getIssueCategoryBreakdown() {
    try {
      const { data: tickets, error } = await supabase
        ?.from('support_tickets')
        ?.select('issue_type');

      if (error) throw error;

      const categoryMap = {};
      tickets?.forEach(ticket => {
        const category = ticket?.issue_type || 'Other';
        categoryMap[category] = (categoryMap?.[category] || 0) + 1;
      });

      return Object.entries(categoryMap)?.map(([category, count]) => ({
        category,
        count,
        percentage: tickets?.length > 0 ? Math.round((count / tickets?.length) * 100 * 10) / 10 : 0
      }))?.sort((a, b) => b?.count - a?.count);
    } catch (error) {
      console.error('Error fetching issue category breakdown:', error);
      throw error;
    }
  },

  // Get ticket trend data (last 30 days)
  async getTicketTrends() {
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo?.setDate(thirtyDaysAgo?.getDate() - 30);

      const { data: tickets, error } = await supabase
        ?.from('support_tickets')
        ?.select('created_at, ticket_status')
        ?.gte('created_at', thirtyDaysAgo?.toISOString());

      if (error) throw error;

      // Group by date
      const trendMap = {};
      tickets?.forEach(ticket => {
        const date = new Date(ticket?.created_at)?.toISOString()?.split('T')?.[0];
        if (!trendMap?.[date]) {
          trendMap[date] = { date, created: 0, resolved: 0 };
        }
        trendMap[date].created++;
        if (ticket?.ticket_status === 'resolved' || ticket?.ticket_status === 'closed') {
          trendMap[date].resolved++;
        }
      });

      return Object.values(trendMap)?.sort((a, b) => new Date(a?.date) - new Date(b?.date));
    } catch (error) {
      console.error('Error fetching ticket trends:', error);
      throw error;
    }
  },

  // Get escalations
  async getEscalations() {
    try {
      const { data, error } = await supabase
        ?.from('ticket_escalations')
        ?.select('*')
        ?.order('escalated_at', { ascending: false });

      if (error) throw error;

      return data?.map(row => ({
        id: row?.id,
        ticketId: row?.ticket_id,
        escalationReason: row?.escalation_reason,
        escalatedFrom: row?.escalated_from,
        escalatedTo: row?.escalated_to,
        escalatedAt: row?.escalated_at,
        resolvedAt: row?.resolved_at,
        resolutionNotes: row?.resolution_notes
      })) || [];
    } catch (error) {
      console.error('Error fetching escalations:', error);
      throw error;
    }
  }
};

export default supportTicketService;